/*
    Simple Pagination Component

    PROPS -- REQUIRED

    current                      current active page
    total                        total items that are there
    max                          max number of items to display on each page
    onChange(page, start, max)   Called on event when user clicks page
*/
import React, { Component, Fragment } from 'react';

import PropTypes from 'prop-types';
import NumberFormatter from '../../formatter/NumberFormatter';
import PageSizeSelect from './SelectPageSize';

const DEFAULT_MAX = 25;

export class Pagination extends Component {
   constructor(props) {
      super(props);
      this.onPageChange = this.onPageChange.bind(this);
      this.onPageSizeChange = this.onPageSizeChange.bind(this);
   }

   onPageChange(event, page) {
      event.preventDefault();
      if (this.props.onChange) {
         const { max } = this.props;
         const start = ((page - 1) * max);
         this.props.onChange(page, start, max);
      }
   }

   // handle page change
   onPageSizeChange(pageSize) {
      this.props.onChange(1, 0, pageSize);
   }

   render() {
      const max = this.props.max ? this.props.max : DEFAULT_MAX;
      const totalPages = Math.floor(this.props.total / max) + ((this.props.total % max) > 0 ? 1 : 0);
      const current = +(this.props.current);
      const inferredVisibleCount = this.props.totalIsExact
         ? Math.max(0, Math.min(max, this.props.total - ((current - 1) * max)))
         : 0;
      const visibleCount = this.props.visibleCount === undefined
         ? inferredVisibleCount
         : +(this.props.visibleCount);
      const rangeStart = visibleCount > 0 ? ((current - 1) * max) + 1 : 0;
      const rangeEnd = visibleCount > 0 ? rangeStart + visibleCount - 1 : 0;

      let displayPages = [...Array(totalPages).keys()];
      let showRightDots = false;
      let showLeftDots = false;

      if (totalPages > 10) {
         if (current - 5 <= 0) {
            showRightDots = true;
            displayPages = displayPages.slice(0, 8);
         } else if (current + 5 > totalPages) {
            showLeftDots = true;
            displayPages = displayPages.slice(totalPages - 8);
         } else {
            showRightDots = true;
            showLeftDots = true;
            displayPages = displayPages.slice(current - 4, current + 3);
         }
      }

      return (
         <div className='clearfix ec1-pagination'>
            {this.props.itemLabel && visibleCount > 0 && (
               <div className='text-muted small ec1-pagination-range'>
                  {this.props.totalIsExact
                     ? (
                        <>
                           Showing
                           {' '}
                           <NumberFormatter value={rangeStart}/>
                           –
                           <NumberFormatter value={rangeEnd}/>
                           {' of '}
                           <NumberFormatter value={this.props.total}/>
                           {this.props.totalIsCapped ? '+' : ''}
                           {' '}
                           {this.props.itemLabel}
                        </>
                     )
                     : (
                        <>
                           Showing
                           {' '}
                           {this.props.itemLabel}
                           {' '}
                           <NumberFormatter value={rangeStart}/>
                           –
                           <NumberFormatter value={rangeEnd}/>
                        </>
                     )}
               </div>
            )}
            <div className='float-left mr-3' style={{ width: '75px' }}>
               <PageSizeSelect value={max.toString()} onChange={this.onPageSizeChange}/>
            </div>
            {
               this.props.total > 0
                    && (
                       <div className='clearfix float-right'>
                          <div className='float-right ec1-pagination-pages'>
                             <nav>
                                <ul className='pagination'>
                                   <li className={`page-item ${ current === 1 ? 'disabled ' : '' }`}>
                                      <a
                                         className='page-link'
                                         href='#'
                                         aria-label='Previous'
                                         onClick={
                                            (event) =>
                                               this.onPageChange(event, current - 1 > 1 ? current - 1 : 1)
                                         }
                                      >
                                         <span aria-hidden='true'>
                                            <i className='fa fa-chevron-left' />
                                         </span>
                                      </a>
                                   </li>
                                   {
                                      showLeftDots
                                           && (
                                              <>
                                                 <li className='page-item'>
                                                    <a
                                                       className='page-link'
                                                       href='#'
                                                       onClick={
                                                          (event) =>
                                                             this.onPageChange(event, 1)
                                                       }
                                                    >
                                                       <span aria-hidden='true'>1</span>
                                                    </a>
                                                 </li>
                                                 <li className='page-item disabled'>
                                                    <a className='page-link'>
                                                       <i className='fal fa-ellipsis-h' />
                                                    </a>
                                                 </li>
                                              </>
                                           )
                                   }

                                   {
                                      displayPages.map(
                                         (item) => {
                                            return (
                                               <li
                                                  className={`page-item ${ +current === +(item + 1) ? 'active' : '' }`}
                                                  key={item}
                                               >
                                                  <a
                                                     className='page-link'
                                                     href='#'
                                                     onClick={
                                                        (event) =>
                                                           this.onPageChange(event, item + 1)
                                                     }
                                                  >
                                                     <NumberFormatter value={item + 1} />
                                                  </a>
                                               </li>
                                            );
                                         },
                                      )
                                   }
                                   {showRightDots
                                           && (
                                              <>
                                                 <li className='page-item disabled'>
                                                    <a className='page-link'>
                                                       <i className='fal fa-ellipsis-h' />
                                                    </a>
                                                 </li>
                                                 <li className='page-item'>
                                                    <a
                                                       className='page-link'
                                                       href='#'
                                                       onClick={(event) => this.onPageChange(event, totalPages)}
                                                    >
                                                       <span aria-hidden='true'><NumberFormatter value={totalPages} /></span>
                                                    </a>
                                                 </li>
                                              </>
                                           )}
                                   <li className={`page-item ${ current === totalPages ? 'disabled ' : ''}`}>
                                      <a
                                         className='page-link'
                                         href='#'
                                         aria-label='Next'
                                         onClick={(event) => this.onPageChange(event, current + 1 < totalPages ? current + 1 : totalPages)}
                                      >
                                         <span aria-hidden='true'><i className='fa fa-chevron-right' /></span>
                                      </a>
                                   </li>
                                </ul>
                             </nav>
                          </div>
                       </div>
                    )
            }
         </div>
      );
   }
}

Pagination.propTypes = {
   onChange: PropTypes.func.isRequired,
   max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
   total: PropTypes.number,
   current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   visibleCount: PropTypes.number,
   totalIsExact: PropTypes.bool,
   // when true the total is a capped floor and renders with a trailing plus ("1,000+")
   totalIsCapped: PropTypes.bool,
   itemLabel: PropTypes.string,
};

Pagination.defaultProps = {
   onChange: null,
   max: DEFAULT_MAX,
   total: 1,
   current: 1,
   visibleCount: undefined,
   totalIsExact: true,
   totalIsCapped: false,
   itemLabel: 'rows',
};

export default Pagination;
